import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Clock, Plus, Search, ArrowLeft, Loader2, Trash2, Settings, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

const CoursesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all'); // 'all' or 'favorites'
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({ topic: '', complexity: 'normal' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [deletedDialogOpen, setDeletedDialogOpen] = useState(false);
  const [deletedCourses, setDeletedCourses] = useState([]);

  useEffect(() => {
    loadAllData();
  }, []);
  
  const loadAllData = async () => {
    try {
      // Parallelize ALL API calls
      const [coursesRes, progressRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/progress`, { withCredentials: true }),
        axios.get(`${API}/subscription/check`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      // Map progress to courses
      const coursesWithProgress = coursesRes.data.map(course => {
        const prog = progressRes.data.find(p => p.course_id === course.id);
        return {
          ...course,
          progress: prog?.completion_percentage || 0
        };
      });
      
      setCourses(coursesWithProgress);
      
      // Set subscription
      if (subscriptionRes.data) {
        setSubscription(subscriptionRes.data);
      }
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = courses;
    
    // Apply favorites filter
    if (filterTab === 'favorites') {
      filtered = filtered.filter(course => course.is_favorite);
    }
    
    // Apply search filter
    filtered = filtered.filter(course =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.topic.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredCourses(filtered);
  }, [searchTerm, courses, filterTab]);

  const loadCourses = async () => {
    try {
      const [coursesRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/progress`, { withCredentials: true })
      ]);
      
      // Map progress to courses
      const coursesWithProgress = coursesRes.data.map(course => {
        const prog = progressRes.data.find(p => p.course_id === course.id);
        return {
          ...course,
          progress: prog?.completion_percentage || 0
        };
      });
      
      setCourses(coursesWithProgress);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCourse = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const response = await axios.post(
        `${API}/courses/generate`,
        { topic: newCourse.topic, complexity: newCourse.complexity },
        { withCredentials: true }
      );

      setDialogOpen(false);
      // Reload courses list and stay on courses page
      await loadCourses();
      // Navigate to courses page (dashboard) instead of course detail
      navigate('/courses');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to generate course';
      
      // Check if it's a trial limit error
      if (errorMessage.includes('Trial limit reached') || errorMessage.includes('subscribe')) {
        toast.error(errorMessage, { duration: 6000 });
        // Redirect to subscription page after a delay
        setTimeout(() => {
          navigate('/subscription');
        }, 2000);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setGenerating(false);
    }
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      normal: 'bg-blue-100 text-blue-700',
      scholar: 'bg-purple-100 text-purple-700',
      // Legacy support for existing courses
      novice: 'bg-green-100 text-green-700',
      proficient: 'bg-amber-100 text-amber-700'
    };
    return colors[complexity] || colors.normal;
  };

  const handleDeleteClick = (course, e) => {
    e.stopPropagation(); // Prevent card click
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return;

    try {
      await axios.delete(`${API}/courses/${courseToDelete.id}`, { withCredentials: true });
      loadCourses();
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const toggleFavorite = async (course, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const response = await axios.post(`${API}/courses/${course.id}/favorite`, {}, { withCredentials: true });
      
      // Update local state
      setCourses(prevCourses =>
        prevCourses.map(c =>
          c.id === course.id
            ? { ...c, is_favorite: response.data.is_favorite }
            : c
        )
      );
      
      toast.success(response.data.is_favorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const loadDeletedCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/deleted/recent`, { withCredentials: true });
      setDeletedCourses(response.data);
    } catch (error) {
      toast.error('Failed to load deleted courses');
    }
  };

  const recoverCourse = async (courseId) => {
    try {
      await axios.post(`${API}/courses/${courseId}/recover`, {}, { withCredentials: true });
      toast.success('Course recovered successfully');
      loadCourses();
      loadDeletedCourses();
    } catch (error) {
      toast.error('Failed to recover course');
    }
  };

  const permanentlyDelete = async (courseId) => {
    if (!confirm('Permanently delete this course? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}/permanent`, { withCredentials: true });
      toast.success('Course permanently deleted');
      loadDeletedCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const deleteAllCourses = async () => {
    if (!confirm(`Permanently delete all ${deletedCourses.length} courses? This cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(`${API}/courses/deleted/all`, { withCredentials: true });
      toast.success(response.data.message);
      loadDeletedCourses();
    } catch (error) {
      toast.error('Failed to delete courses');
    }
  };

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <BookOpen className={`w-6 h-6 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
                <span className={`text-xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Courses</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                title="Profile & Settings"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (open) {
                  // Clear the form when dialog opens
                  setNewCourse({ topic: '', complexity: 'normal' });
                }
              }}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700" data-testid="create-course-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Course
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" data-testid="create-course-dialog">
                <DialogHeader>
                  <DialogTitle className={isDarkMode ? 'text-amber-100' : 'text-amber-900'}>Generate New Course</DialogTitle>
                  <DialogDescription className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                    AI will create a comprehensive curriculum based on your topic and level
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleGenerateCourse} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="topic">Historical Topic</Label>
                    <Input
                      id="topic"
                      data-testid="course-topic-input"
                      placeholder="e.g., Roman Empire, World War II, Ancient Egypt"
                      value={newCourse.topic}
                      onChange={(e) => setNewCourse({ ...newCourse, topic: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="complexity">Reading Level</Label>
                    <Select
                      value={newCourse.complexity}
                      onValueChange={(value) => setNewCourse({ ...newCourse, complexity: value })}
                    >
                      <SelectTrigger data-testid="complexity-select">
                        <SelectValue placeholder="Select reading level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal" data-testid="complexity-normal">Normal - Clear & Accessible</SelectItem>
                        <SelectItem value="scholar" data-testid="complexity-scholar">Scholar - Academic & In-Depth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={generating}
                    data-testid="generate-course-submit-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Course'
                    )}
                  </Button>
                  {generating && (
                    <p className="text-sm text-amber-600 text-center mt-3">
                      Course generation may take 1-3 minutes depending on complexity
                    </p>
                  )}
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 page-enter">
        {/* Trial Limit Banner */}
        {subscription?.is_trial && (
          <div className="mb-6 bg-gradient-to-r from-amber-100 to-orange-100 border-l-4 border-amber-600 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-amber-900">
                  Free Trial: {Math.max(0, 2 - (subscription?.subscription?.courses_generated || 0))} of 2 courses remaining
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Subscribe to create unlimited courses and unlock all features
                </p>
              </div>
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className={`mb-6 flex gap-4 border-b ${isDarkMode ? 'border-amber-700' : 'border-amber-200'}`}>
          <button
            onClick={() => setFilterTab('all')}
            className={`pb-3 px-4 font-medium transition-colors ${
              filterTab === 'all'
                ? `${isDarkMode ? 'text-amber-400' : 'text-amber-700'} border-b-2 ${isDarkMode ? 'border-amber-500' : 'border-amber-600'}`
                : `${isDarkMode ? 'text-amber-500 hover:text-amber-400' : 'text-amber-500 hover:text-amber-700'}`
            }`}
            data-testid="filter-all"
          >
            Courses
          </button>
          <button
            onClick={() => setFilterTab('favorites')}
            className={`pb-3 px-4 font-medium transition-colors flex items-center gap-2 ${
              filterTab === 'favorites'
                ? `${isDarkMode ? 'text-amber-400' : 'text-amber-700'} border-b-2 ${isDarkMode ? 'border-amber-500' : 'border-amber-600'}`
                : `${isDarkMode ? 'text-amber-500 hover:text-amber-400' : 'text-amber-500 hover:text-amber-700'}`
            }`}
            data-testid="filter-favorites"
          >
            <Star className="w-4 h-4" />
            Favorites
          </button>
          <button
            onClick={() => {
              loadDeletedCourses();
              setDeletedDialogOpen(true);
            }}
            className={`pb-3 px-4 font-medium transition-colors flex items-center gap-2 ${isDarkMode ? 'text-amber-500 hover:text-amber-400' : 'text-amber-500 hover:text-amber-700'}`}
            data-testid="recently-deleted-btn"
          >
            <Trash2 className="w-4 h-4" />
            Recently Deleted
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-600 w-5 h-5" />
            <Input
              placeholder="Search courses..."
              className="pl-10 border-amber-200 focus:border-amber-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="course-search-input"
            />
          </div>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <Card className="card-glow p-12 text-center" data-testid="no-courses-message">
            <BookOpen className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>No courses yet</h3>
            <p className={`mb-6 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Create your first AI-generated history course</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-first-course-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Course
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
            {filteredCourses.map((course, index) => (
              <Card
                key={course.id}
                className={`card-glow card-interactive border-amber-200 bg-white/90 backdrop-blur-sm cursor-pointer relative opacity-0 animate-slide-up animate-stagger-${Math.min(index + 1, 6)}`}
                onClick={() => navigate(`/course/${course.id}`)}
                data-testid={`course-card-${course.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getComplexityColor(course.complexity)}`}>
                      {course.complexity.charAt(0).toUpperCase() + course.complexity.slice(1)}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center text-amber-600">
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="text-sm">{course.total_hours}h</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${
                          course.is_favorite
                            ? 'text-yellow-500 hover:text-yellow-600'
                            : 'text-gray-400 hover:text-yellow-500'
                        }`}
                        onClick={(e) => toggleFavorite(course, e)}
                        data-testid={`favorite-course-${course.id}`}
                        title={course.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className={`w-4 h-4 ${course.is_favorite ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteClick(course, e)}
                        data-testid={`delete-course-${course.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-xl text-amber-900 line-clamp-2">{course.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-amber-700 line-clamp-3">
                    {course.description}
                  </CardDescription>
                  <div className="mt-4 pt-4 border-t border-amber-200 space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-amber-600">
                        <span>Progress</span>
                        <span className="font-semibold">{Math.round(course.progress || 0)}%</span>
                      </div>
                      <div className="w-full bg-amber-100 rounded-full h-2">
                        <div 
                          className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${course.progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-amber-600">
                      <span>{course.lessons.length} Lessons</span>
                      <span>→</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course?</AlertDialogTitle>
            <AlertDialogDescription>
              This course will be moved to Recently Deleted for 30 days. You can recover it during this time or it will be permanently deleted after 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recently Deleted Dialog */}
      <Dialog open={deletedDialogOpen} onOpenChange={setDeletedDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-amber-100' : ''}`}>
              <Trash2 className="w-5 h-5 text-amber-600" />
              Recently Deleted
            </DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-amber-300' : ''}>
              Courses deleted in the last 30 days. Recover them or they'll be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          {deletedCourses.length > 0 && (
            <div className="flex justify-end -mt-2 mb-2">
              <Button
                size="sm"
                variant="outline"
                onClick={deleteAllCourses}
                className={`border-red-300 text-red-600 ${isDarkMode ? 'hover:bg-red-900/30 hover:text-red-400 hover:border-red-400' : 'hover:bg-red-50'}`}
              >
                Delete All
              </Button>
            </div>
          )}
          
          <div className="space-y-4">
            {deletedCourses.length === 0 ? (
              <div className="text-center py-12">
                <Trash2 className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-amber-400' : 'text-gray-300'}`} />
                <p className={isDarkMode ? 'text-amber-300' : 'text-gray-500'}>No recently deleted courses</p>
              </div>
            ) : (
              deletedCourses.map((course) => (
                <Card key={course.id} className="card-glow border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{course.title}</h3>
                        <p className={`text-sm mb-2 ${isDarkMode ? 'text-amber-300' : 'text-gray-600'}`}>
                          Deleted {new Date(course.deleted_at).toLocaleDateString()}
                        </p>
                        <div className={`flex items-center gap-4 text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          <span>{course.lessons?.length || 0} Lessons</span>
                          <span className="capitalize">{course.complexity} Level</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => recoverCourse(course.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Recover
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => permanentlyDelete(course.id)}
                          className={`border-red-300 text-red-600 ${isDarkMode ? 'hover:bg-red-900/30 hover:text-red-400 hover:border-red-400' : 'hover:bg-red-50'}`}
                        >
                          Delete Forever
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursesPage;